import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ScratchCard from './ScratchCard';
import ModifCartePanel from './ModifCartePanel';
import { QRCodeSVG } from 'qrcode.react';
import Typography from '@mui/material/Typography';
import { useT, useLang } from '../i18n';
import { API_BASE } from '../utils/apiBase';

// Langue de la carte pour le destinataire : « auto » = aucun paramètre dans le lien (la carte s'ouvre dans la
// langue du téléphone de la personne qui la reçoit, comme avant) ; sinon le lien porte ?lang=xx.
const AUTO = 'auto';
// Langues proposées à l'acheteur pour le destinataire, parmi celles qui sont ACTIVES sur le site : l'italien et le
// portugais n'apparaissent donc que le jour où ils sont publiés (REACT_APP_I18N_LANGS).
const OFFERED_LANGS = ['fr', 'en', 'de', 'es', 'it', 'pt'];
const langStorageKey = (id) => `jm_carte_lang_${id}`;
const readRecipientLang = (id) => {
  try { return window.localStorage.getItem(langStorageKey(id)) || AUTO; } catch (e) { return AUTO; }
};
const storeRecipientLang = (id, code) => {
  try {
    if (code === AUTO) window.localStorage.removeItem(langStorageKey(id));
    else window.localStorage.setItem(langStorageKey(id), code);
  } catch (e) { /* navigation privée : le choix n'est simplement pas mémorisé */ }
};

const ScratchCardPage = () => {
  // Langue de cette page : celle de l'acheteur (adresse, choix, navigateur). Le message de la carte n'est jamais traduit.
  const t = useT('scratch-card');
  const { languages: siteLanguages } = useLang();
  const languages = siteLanguages.filter((l) => OFFERED_LANGS.includes(l.code));
  const { id } = useParams();
  const [chosenLang, setChosenLang] = useState(() => readRecipientLang(id));
  // Un choix mémorisé pour une langue que le site ne propose plus (ou sans sélecteur) = automatique.
  const recipientLang = languages.length > 1 && languages.some((l) => l.code === chosenLang) ? chosenLang : AUTO;
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [textSize, setTextSize] = useState(24);
  const [textColor, setTextColor] = useState('#000000');
  const [message, setMessage] = useState('');
  const [titre, setTitre] = useState('');
  const [brushSize, setBrushSize] = useState(30);
  const [marginTop, setMarginTop] = useState(0);
  const [textHeight, setTextHeight] = useState(0); // Nouvelle valeur pour la hauteur
  const [lineHeight, setLineHeight] = useState(45); // Nouvelle valeur pour l'interligne
  const [isSubmitting, setIsSubmitting] = useState(false); // Indicateur de soumission
  const [submitSuccess, setSubmitSuccess] = useState(null); // Statut de la soumission

  // Gérer le texte du bouton (état : 'idle' | 'sending' | 'saved' | 'error', le texte est traduit à l'affichage)
  const [buttonState, setButtonState] = useState('idle');
  const buttonText = t(`scratch-card.editor.button.${buttonState}`);

  // Fetch initial data
  useEffect(() => {
    const API_URL = `${API_BASE}/apiEtsy.php?cleApi=34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75&service=getCarte&id=${id}`;
    
    fetch(API_URL)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        setConfig(data);
        setMessage(decodeHtml(data.message || ''));
        setTitre(decodeHtml(data.titre || ''));
        setTextSize(data.taille_text || 24);
        setTextColor(data.couleur_text || '#000000');
        setBrushSize(30);
        setMarginTop(data.margin_top_gratte || 0);
        setLineHeight(data['line-height'] || 45); // Récupération de l'interligne
        setTextHeight(data['text-margin-top'] || 0); // Récupération de l'interligne
        setLoading(false);
      })
      .catch(error => {
        setError(error);
        setLoading(false);
      });
  }, [id]);

  const decodeHtml = (html) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  const getRandomInt = (min, max) => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  // Nouvelle fonction pour obtenir la date actuelle (format YYYY-MM-DD)
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Mois avec deux chiffres
    const day = String(today.getDate()).padStart(2, '0'); // Jour avec deux chiffres
    return `${year}-${month}-${day}`;
  };

  // Fonction pour soumettre les modifications via l'API en GET
  const handleSubmit = (e) => {
    e.preventDefault(); // Empêche le rechargement de la page
    setIsSubmitting(true); // Démarrage du chargement
    setSubmitSuccess(null); // Réinitialiser le succès/erreur
    setButtonState('sending'); // Changement du texte du bouton

    const updatedCarte = {
      id,
      message, // Pas besoin d'encodeURIComponent ici
      textSize,
      textColor, // Pas besoin d'encodeURIComponent ici
      marginTop,
      textHeight,
      lineHeight,
      dateCreation: getCurrentDate(), // On met à jour la date actuelle
    };

    // Construction de l'URL avec les paramètres GET
    const queryParams = new URLSearchParams(updatedCarte).toString();
    const url = `${API_BASE}/apiEtsy.php?cleApi=34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75&service=upCarte&${queryParams}`;

    console.log('URL envoyée à l\'API :', url);

    // Envoi de la requête GET
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setIsSubmitting(false); // Arrêt du chargement
        console.log('Réponse de l\'API :', data);

        if (data.success) {
          setSubmitSuccess(true); // Affichage de la confirmation de succès
          setButtonState('saved'); // Changement du texte du bouton
        } else {
          setSubmitSuccess(false); // Affichage de l'erreur
          setButtonState('error'); // Texte du bouton en cas d'erreur
        }
      })
      .catch(error => {
        setIsSubmitting(false); // Arrêt du chargement
        setSubmitSuccess(false); // Affichage de l'erreur
        setButtonState('error'); // Texte du bouton en cas d'erreur
        console.error('Erreur lors de l\'enregistrement des modifications', error);
      });
  };

  // Lien à partager : ?lang=xx seulement si l'acheteur a imposé une langue (automatique = lien d'avant).
  const previewURL = `https://joli-mot.fr/carte-a-gratter/${id}&${getRandomInt(123456, 45943284588234)}${recipientLang !== AUTO ? `?lang=${recipientLang}` : ''}`;

  const chooseRecipientLang = (code) => {
    setChosenLang(code);
    storeRecipientLang(id, code);
  };

  if (loading) return <div>{t('scratch-card.loadingLegacy')}</div>;
  if (error) return <div>{t('scratch-card.errorPrefixLegacy', { message: error.message })}</div>;

  return (
    <div className="scratch-card-page">
      {/* Page d'édition perso du client (pas censée être partagée), mais gardée neutre par
          sécurité : hérite sinon du <title> par défaut du site ("Cartes à Gratter Numériques…")
          qui dévoile la surprise si le lien est transmis par erreur. */}
      <Helmet>
        <title>{t('scratch-card.editor.pageTitle')}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="page-content">
        <div className="scratch-card-section">
          <p className='info'>{t.rich('scratch-card.editor.intro')}</p>
          <ScratchCard
            width={400}
            height={400}
            gratteSrc={`/assets_react/grattes/${config.gratte}.png`}
            brushSize={brushSize}
            text={message}
            titre={titre}
            imageSrc={`/assets_react/visuels/${config.visuel}`}
            textSize={textSize}
            textColor={textColor}
            marginTop={marginTop}
            textHeight={textHeight} // Passer l'interligne à ScratchCard
            lineHeight={lineHeight} // Passer l'interligne à ScratchCard
          />
        </div>
        <div className="modif-carte-section">
          <ModifCartePanel
            textSize={textSize}
            setTextSize={setTextSize}
            textColor={textColor}
            setTextColor={setTextColor}
            message={message}
            setMessage={setMessage}
            textHeight={textHeight}
            lineHeight={lineHeight}
            setTextHeight={setTextHeight}
            setLineHeight={setLineHeight}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            buttonText={buttonText} // Passer le texte du bouton et l'état
          />

          {/* Indicateur visuel de soumission */}
          {submitSuccess === true && <p style={{ color: 'green' }}>{t('scratch-card.editor.successMessage')}</p>}
          {submitSuccess === false && <p style={{ color: 'red' }}>{t('scratch-card.editor.errorMessage')}</p>}

          <div className="share-info">
            {/* Choix de la langue de la carte pour le destinataire : n'apparaît que si le site propose plusieurs langues. */}
            {languages.length > 1 && (
              <div className="recipient-lang" style={{ marginBottom: 20 }}>
                <label htmlFor="recipientLang" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
                  {t('scratch-card.editor.langLabel')}
                </label>
                <select
                  id="recipientLang"
                  value={recipientLang}
                  onChange={(e) => chooseRecipientLang(e.target.value)}
                  style={{ padding: '8px 10px', fontSize: 16, maxWidth: '100%' }}
                >
                  <option value={AUTO}>{t('scratch-card.editor.langAuto')}</option>
                  {languages.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
                <p style={{ marginTop: 6, fontSize: 14 }}>
                  {recipientLang === AUTO ? t('scratch-card.editor.langHelpAuto') : t('scratch-card.editor.langHelpChosen')}
                </p>
              </div>
            )}
            <p>{t('scratch-card.editor.shareIntro')}</p>
            <Typography variant="h3" color="secondary" gutterBottom>{previewURL}</Typography>
            <br />
            <br />
            <p>{t('scratch-card.editor.qrIntro')}</p>
            <div className="qrcode-container">
              <QRCodeSVG value={previewURL} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScratchCardPage;
